import { Col, FormInput, FormRadioGroup, Row, InPageAlert } from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import styles from '../PlantItemDetails.module.scss';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';
import { RefData } from 'plantItemNew/constants/refData';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';

export const Lift = React.forwardRef((props, ref) => {
  const { value: savedPlantDetails = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;

  const {
    subType: savedSubType = '',
    otherSubType: savedOtherSubType = '',
    manufacturer: savedManufacturer = '',
    serialNumber: savedSerialNumber = '',
    modelNameOrNumber: savedModelNameOrNumber = '',
    yearOfManufacture: savedYearOfManufacture = '',
    accessibilityAtLocation: savedAccessibilityAtLocation = '',
    specificLocation: savedSpecificLocation = '',
    uniqueIdentifier: savedUniqueIdentifier = '',
  } = savedPlantDetails;

  const subTypeOptions = Array.isArray(refData[RefData.lift.subTypeOptions])
    ? refData[RefData.lift.subTypeOptions].map((e) => ({
        value: e.value,
        text: e.description,
      }))
    : [];

  const liftTypeLocationOptions = Array.isArray(refData[RefData.lift.locationTypeOptions])
    ? refData[RefData.lift.locationTypeOptions].map((e) => {
        let helpText = '';
        let description = e.description;

        let parse = e.description.match(/(.*)\((.*)\)/);
        if (Array.isArray(parse) && parse.length == 3) {
          helpText = parse[2];
          description = parse[1];
        }

        return {
          value: e.value,
          label: description,
          clarify: helpText,
        };
      })
    : [];

  const [subType, setSubType] = useState(savedSubType);
  const [otherSubType, setOtherSubType] = useState(savedOtherSubType);
  const [manufacturer, setManufacturer] = useState(savedManufacturer);
  const [modelNameOrNumber, setModelNameOrNumber] = useState(savedModelNameOrNumber);
  const [yearOfManufacture, setYearOfManufacture] = useState(savedYearOfManufacture);
  const [serialNumber, setSerialNumber] = useState(savedSerialNumber);
  const [uniqueIdentifier, setUniqueIdentifier] = useState(savedUniqueIdentifier);
  const [accessibilityAtLocation, setAccessibilityAtLocation] = useState(savedAccessibilityAtLocation);

  const [specificLocation, setSpecificLocation] = useState(savedSpecificLocation);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({
    subType: false,
    subType_other: false,
    manufacturer: false,
    modelNameOrNumber: false,
    yearOfManufacture: false,
    serialNumber: false,
    uniqueIdentifier: false,
    accessibilityAtLocation: false,
    specificLocation: false,
  });

  const [errorSummary, setErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'subType',
      fieldValue: subType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'subType_other',
      fieldValue: otherSubType,
      isValid: (v) => !isEmpty(v) || subType !== 'Other (please describe)',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'manufacturer',
      fieldValue: manufacturer,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'modelNameOrNumber',
      fieldValue: modelNameOrNumber,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'yearOfManufacture',
      fieldValue: yearOfManufacture,
      isValid: (v) => !isEmpty(v) && Number.isInteger(+v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter a valid year'),
    },
    {
      fieldName: 'serialNumber',
      fieldValue: serialNumber,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'accessibilityAtLocation',
      fieldValue: accessibilityAtLocation,
      isValid: (v) => !isEmpty(v) && !v.includes('Single dwelling'),
      errorMessage: (v) =>
        isEmpty(v)
          ? 'Please select an option'
          : 'For Private residence lifts , Please  complete a paper based form available on the SafeWork NSW Website',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let itemDetails = {
      subType,
      otherSubType,
      manufacturer,
      serialNumber,
      modelNameOrNumber,
      yearOfManufacture,
      locationType: 'Fixed',
      accessibilityAtLocation,
      specificLocation,
      uniqueIdentifier,
    };
    onChange(itemDetails);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    subType,
    otherSubType,
    manufacturer,
    modelNameOrNumber,
    yearOfManufacture,
    serialNumber,
    uniqueIdentifier,
    accessibilityAtLocation,
    specificLocation,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <Row>
        <Col span={8}>
          <InPageAlert
            variant="info"
            title="If you are registering a lift for a private residence, please complete the PDF form available on the SafeWork NSW website."
            headingLevel="h4"
          />

          <RadioOrDropdownSelector
            id="subType"
            options={subTypeOptions}
            title={PlantItemDetailsPage.itemDetails.liftTypeLabel}
            errorMessage={errors?.subType?.errorMessage}
            hasError={showErrors && errors?.subType?.invalid}
            onChange={(v) => {
              setSubType(v);
              setOtherSubType('');
            }}
            value={subType}
            showExtraInput={subType === 'Other (please describe)'}
            onChangeExtraInput={setOtherSubType}
            extraInputValue={otherSubType}
            extraInputErrorMessage={errors?.subType_other?.errorMessage}
            extraInputHasError={showErrors && errors?.subType_other?.invalid}
            extraInputTitle="Other please describe"
            margin={{ top: 'md' }}
            inputWidth="lg"
            maxOptionsLengthForRadio={2}
          />

          <FormInput
            id="manufacturer"
            label={PlantItemDetailsPage.itemDetails.manufacturerLabel}
            errorMessage={errors?.manufacturer?.errorMessage}
            hasError={showErrors && errors?.manufacturer?.invalid}
            value={manufacturer}
            onChange={(e, { value }) => setManufacturer(value)}
            inputWidth="lg"
            margin={{ top: 'md' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.modelNameOrNumberLabel}
            helpMessage="Supplier’s model name or number can be provided"
            id="modelNameOrNumber"
            errorMessage={errors?.modelNameOrNumber?.errorMessage}
            hasError={showErrors && errors?.modelNameOrNumber?.invalid}
            value={modelNameOrNumber}
            onChange={(e, { value }) => setModelNameOrNumber(value)}
            inputWidth="lg"
            margin={{ top: 'md' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.yearOfManufactureLabel}
            id="yearOfManufacture"
            errorMessage={errors?.yearOfManufacture?.errorMessage}
            helpMessage="If unknown, enter an approximate year of manufacture"
            hasError={showErrors && errors?.yearOfManufacture?.invalid}
            value={yearOfManufacture}
            onChange={(e, { value }) => setYearOfManufacture(value)}
            inputWidth="xs"
            placeholder="YYYY"
            mask={[/\d/, /\d/, /\d/, /\d/]}
            margin={{ top: 'md' }}
          />

          <div className={`${styles.labelMsg} ${styles.marginTopOneHalf}`}>
            {PlantItemDetailsPage.itemDetails.serialNumberLabel}
          </div>
          <div className={styles.helpMsg}>
            Unique identifying number issued by the manufacturer. <br />
            Note: Lifts, escalators and moving walks may have a contract number.
          </div>
          <FormInput
            id="serialNumber"
            value={serialNumber}
            onChange={(e, { value }) => setSerialNumber(value)}
            errorMessage={errors?.serialNumber?.errorMessage}
            hasError={showErrors && errors?.serialNumber?.invalid}
            inputWidth="lg"
            margin={{ top: 'none' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.specificLocationLabel}
            helpMessage="This is to help you identify where your item is specifically located at your address"
            placeholder="eg. Building name or room the item is stored in."
            value={specificLocation}
            onChange={(e, { value }) => setSpecificLocation(value)}
            margin={{ top: 'md' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.uniqueIdentifierLabel}
            helpMessage="You can provide your own unique reference number to help identify the item"
            value={uniqueIdentifier}
            onChange={(e, { value }) => setUniqueIdentifier(value)}
            inputWidth="lg"
            margin={{ top: 'md' }}
          />

          <FormRadioGroup
            id="liftAccessibility"
            name="liftAccessibility"
            legend={PlantItemDetailsPage.itemDetails.liftAccessibilityLabel}
            vertical={true}
            options={liftTypeLocationOptions}
            onChange={setAccessibilityAtLocation}
            value={accessibilityAtLocation}
            errorMessage={errors?.accessibilityAtLocation?.errorMessage}
            hasError={showErrors && errors?.accessibilityAtLocation?.invalid}
            margin={{ top: 'md' }}
          />
        </Col>
      </Row>
    </>
  );
});

Lift.displayName = 'Lift';
